import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';

interface PasswordResetPageProps {
    openAuthModal: (view: 'login' | 'register', message?: string) => void;
}

const PasswordResetPage: React.FC<PasswordResetPageProps> = ({ openAuthModal }) => {
    const [token, setToken] = useState<string | null>(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const resetToken = params.get('resetToken');
        if (!resetToken) {
            setError("Invalid or missing password reset token. Please request a new link.");
        }
        setToken(resetToken);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (!token) {
            setError("Missing reset token.");
            return;
        }

        setLoading(true);
        try {
            await api.post('/api/v1/auth/reset-password', { token, newPassword: password });
            setSuccess(true);
            // Clear the token from URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
            if(err instanceof Error) setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold text-green-700 mb-4">Password Updated!</h2>
                <p className="text-gray-600 mb-6">Your password has been reset successfully. You can now log in with your new password.</p>
                <button onClick={() => openAuthModal('login')} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Go to Login</button>
            </div>
        );
    }
    
    return (
        <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Set New Password</h2>
            <form onSubmit={handleSubmit}>
                {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="password">New Password</label>
                    <input 
                        id="password" 
                        type="password" 
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
                        required 
                        disabled={!token}
                    />
                </div>
                 <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="confirm-password">Confirm New Password</label>
                    <input 
                        id="confirm-password" 
                        type="password" 
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
                        required 
                        disabled={!token}
                    />
                </div>
                
                <button 
                    type="submit" 
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 transition"
                    disabled={loading || !token}
                >
                    {loading ? "Resetting..." : "Reset Password"}
                </button>
            </form>
        </div>
    );
};

export default PasswordResetPage;